/* import __COLOCATED_TEMPLATE__ from './help-center-audience-filter.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { audienceRoles } from 'embercom/models/data/matching-system/matching-constants';
import { audienceRoleLabel } from 'embercom/helpers/audience-types';

interface Args {
  doSearch: (audienceTypes?: string[]) => void;
  selectedValues?: string[];
  activeFilter?: string;
  removeFilter: () => void;
}

interface Signature {
  Args: Args;
}

interface SelectOption {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class HelpCenterAudienceFilter extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked selectedValues: string[] = this.args.selectedValues || [];
  lastSyncedPropsString = JSON.stringify([...(this.args.selectedValues || [])].sort());

  @action
  syncWithProps() {
    let propsValues = this.args.selectedValues || [];
    let propsString = JSON.stringify([...propsValues].sort());

    if (this.lastSyncedPropsString === propsString) {
      return;
    }

    let currentString = JSON.stringify([...this.selectedValues].sort());
    if (currentString !== propsString) {
      this.selectedValues = propsValues;
    }

    this.lastSyncedPropsString = propsString;
  }

  get isOpen() {
    return this.args.activeFilter === 'helpCenterAudienceRoles';
  }

  get isAllAudiencesSelected(): boolean {
    return this.selectedValues.length === 0;
  }

  get items(): SelectOption[] {
    return [
      {
        text: audienceRoleLabel(audienceRoles.users),
        value: audienceRoles.users,
        isSelected: this.selectedValues.includes(audienceRoles.users),
      },
      {
        text: audienceRoleLabel(audienceRoles.visitors),
        value: audienceRoles.visitors,
        isSelected: this.selectedValues.includes(audienceRoles.visitors),
      },
      {
        text: audienceRoleLabel(audienceRoles.leads),
        value: audienceRoles.leads,
        isSelected: this.selectedValues.includes(audienceRoles.leads),
      },
    ];
  }

  get details() {
    if (this.isAllAudiencesSelected) {
      return this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.is-roles', {
        roles: this.intl.t(
          'knowledge-hub.filterable-list.filters.help-center-audience.all-audiences',
        ),
      });
    }

    let selectedItems = this.items.filter((item) => item.isSelected);
    let separator = this.intl.t(
      'knowledge-hub.filterable-list.filters.help-center-audience.or-separator',
    );
    let selectedText = selectedItems.map((item) => item.text).join(separator);
    return this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.is-roles', {
      roles: selectedText,
    });
  }

  @action selectAllAudiences() {
    this.selectedValues = [];
    this.lastSyncedPropsString = '[]';
    this.args.doSearch(undefined);
  }

  @action removeFilter() {
    this.selectAllAudiences();
    this.args.removeFilter();
  }

  @action toggleRole(role: string) {
    let current = [...this.selectedValues];
    if (current.includes(role)) {
      current = current.filter((v) => v !== role);
    } else {
      current.push(role);
    }

    if (current.length === 0) {
      this.selectAllAudiences();
      return;
    }

    this.selectedValues = current;
    this.lastSyncedPropsString = JSON.stringify([...current].sort());
    this.args.doSearch(current);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Filters::HelpCenterAudienceFilter': typeof HelpCenterAudienceFilter;
    'knowledge-hub/filterable-list/filters/help-center-audience-filter': typeof HelpCenterAudienceFilter;
  }
}
